import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMaxLength,
  SpecialCharacter,
  NavigateConst,
  domainForSegment,
  AlphabetAndNumberRegex,
} from 'common';
import { useNavigate,useLocation } from 'react-router-dom';
import {
  ErrorMessage,
  Button,
  Label,
  InputField,
  GlobalError,
  Headings,
  Loader,
  DatePicker,
} from '@sentara-health/optimahealth_web';
import { useForm, FormProvider } from 'react-hook-form';
import { setMaxDate, minYears } from 'common/utils';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';

const ChildPersonalInformation = ({
  formData,
  page,
  setFormData,
  setPage,
}: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loader, setLoader] = useState<boolean>(false);
  const [noContent] = useState<boolean>(false);
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      parentFirstName: formData[page]?.parentFirstName ?? '',
      parentLastName: formData[page]?.parentLastName ?? '',
      childDob: formData[page]?.childDob ?? '',
      childMemberId: formData[page]?.childMemberId ?? '',
      parentEmail: formData[page]?.parentEmail ?? '',
    },
  });

  const {
    handleSubmit,
    register,
    setValue,
    clearErrors,
    formState: { errors, isValid },
  } = methods;

  const onSubmit = async (data: any) => {
    setLoader(true);
    setFormData({ ...formData, [page]: data });
    formSubmissionSucceeded({
      segment: `${domainForSegment}${pathname}`,
      form: AdobeFormConfig['drChildInfoContinue'],
    });

    // Simple navigation to next step
    setTimeout(() => {
      setLoader(false);
      setPage((currPage: number) => currPage + 1);
    }, 500);
  };

  return (
    <FormProvider {...methods}>
      <form
        data-testid="submit_form"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        {loader && <Loader />}
        <div className="row">
          <div className="col-lg-6 col-md-8 col-12 mt-n2" aria-live="polite">
            {noContent && (
              <GlobalError variant="mb-3" dataTestId="serviceError">
                {t('common.serviceErrorRequestId')}
              </GlobalError>
            )}
            
            <span className="visually-hidden me-1">
              {t('common.registrationCurrentPage')} {[page + 1]}
            </span>
            <Headings
              level={1}
              text={t('profile.designatedRepresentative.designatedRepresentativeParentGuardian')}
              variant="semiBold"
              dataTestId="registrationHeading"
              className="mb-4 pb-4"
            />

            <div className="row mb-4">
              <div className="col-12">
                {/* Parent's First Name */}
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="parentFirstNameLabel"
                    htmlFor="parentFirstName"
                  >
                    {t('profile.designatedRepresentative.parentFirstName')}
                  </Label>
                  <InputField
                    id="parentFirstName"
                    data-testid="parentFirstName"
                    maxLength={getMaxLength.name}
                    placeholder={t('activation.firstNamePlaceholder')}
                    className={`form-input ${errors.parentFirstName ? 'error-border' : ''}`}
                    {...register('parentFirstName', {
                      required: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue('parentFirstName', e.target.value.replace(SpecialCharacter, ''));
                      },
                    })}
                  />
                  {errors.parentFirstName && (
                    <ErrorMessage>{t('profile.designatedRepresentative.parentFirstNameError')}</ErrorMessage>
                  )}
                </div>

                {/* Parent's Last Name */}
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="parentLastNameLabel"
                    htmlFor="parentLastName"
                  >
                    {t('profile.designatedRepresentative.parentLastName')}
                  </Label>
                  <InputField
                    id="parentLastName"
                    data-testid="parentLastName"
                    maxLength={getMaxLength.name}
                    placeholder={t('activation.lastNamePlaceholder')}
                    className={`form-input ${errors.parentLastName ? 'error-border' : ''}`}
                    {...register('parentLastName', {
                      required: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue('parentLastName', e.target.value.replace(SpecialCharacter, ''));
                      },
                    })}
                  />
                  {errors.parentLastName && (
                    <ErrorMessage>{t('profile.designatedRepresentative.parentLastNameError')}</ErrorMessage>
                  )}
                </div>

                {/* Child's Date of Birth */}
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="childDobLabel"
                    htmlFor="childDob"
                  >
                    {t('profile.designatedRepresentative.childDateOfBirth')}
                  </Label>
                  <DatePicker
                    selectedValue={formData[page]?.childDob ?? ''}
                    maxLength={getMaxLength.dob}
                    fieldName="childDob"
                    id="childDob"
                    data-testid="childDob"
                    placeholder={t('profile.designatedRepresentative.dateOfBirthPlaceholder')}
                    maxDate={setMaxDate(minYears)}
                    updateValue={(value: string) => {
                      setValue('childDob', value, {
                        shouldValidate: true,
                      });
                      clearErrors('childDob');
                    }}
                    errorClass={errors?.childDob ? 'error-border' : ''}
                    {...register('childDob', {
                      required: true,
                    })}
                  />
                  {errors?.childDob && (
                    <ErrorMessage>{t('profile.designatedRepresentative.childDobError')}</ErrorMessage>
                  )}
                </div>

                {/* Child's Member ID */}
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="childMemberIdLabel"
                    htmlFor="childMemberId"
                  >
                    {t('profile.designatedRepresentative.childMemberId')}
                  </Label>
                  <InputField
                    id="childMemberId"
                    data-testid="childMemberId"
                    maxLength={getMaxLength.memberId}
                    placeholder={t('activation.memberIdPlaceholder')}
                    className={`${errors.childMemberId ? 'error-border' : ''}`}
                    {...register('childMemberId', {
                      required: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue('childMemberId', e.target.value.replace(AlphabetAndNumberRegex, ''));
                      },
                    })}
                  />
                  {errors.childMemberId && (
                    <ErrorMessage>{t('profile.designatedRepresentative.childMemberIdError')}</ErrorMessage>
                  )}
                </div>

                {/* Parent's Email Address */}
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="parentEmailLabel"
                    htmlFor="parentEmail"
                  >
                    {t('profile.designatedRepresentative.parentEmail')}
                  </Label>
                  <InputField
                    id="parentEmail"
                    data-testid="parentEmail"
                    type="email"
                    maxLength={getMaxLength.email}
                    placeholder={t('common.emaillblPlaceholder')}
                    className={`${errors.parentEmail ? 'error-border' : ''}`}
                    {...register('parentEmail', {
                      required: true,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('common.invalidEmail'),
                      },
                    })}
                  />
                  {errors.parentEmail && (
                    <ErrorMessage>
                      {errors.parentEmail.message || t('profile.designatedRepresentative.parentEmailError')}
                    </ErrorMessage>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row pt-4 pb-2">
          <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
            <Button
              dataTestId="continueButton"
              variant="primary_btn"
              className="btn-md-width"
              disabled={!isValid}
              type="submit"
            >
              {t('common.continue')}
            </Button>
          </div>
          <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
            <Button
              dataTestId="cancelButton"
              variant="secondary_btn"
              className="btn-md-width"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                formSubmissionFailed({
                  segment: `${domainForSegment}${pathname}`,
                  form: AdobeFormConfig['drChildInfoCancel'],
                });
                navigate(NavigateConst.signIn);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default ChildPersonalInformation;